import React, { useContext, useMemo } from 'react'
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { User, useFetchReportCreatorsQuery } from '../../../generated'
import { GRAPHQL_QUERY_POLICY, QUERY_PARAMS } from '../../constants'
import { AuthContext } from '../../context/AuthContext'

interface ReportCreatorSelectProps {
  /** A user id, or QUERY_PARAMS.ALL. */
  value: string
  onChange: (value: string) => void
  /** Locked to the logged-in user — an attorney's history only ever holds their own reports. */
  disabled?: boolean
}

const fullName = (user?: Pick<User, 'firstName' | 'lastName' | 'email'> | null) =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || ''

/**
 * "Generated By" filter for the Report History list.
 *
 * Options come from `fetchReportCreators` — the users who have actually generated a report —
 * rather than the full user list. Only a handful of the firm's ~100 active users ever run
 * one, so a paginated picker over everyone would be mostly dead options, and resolving the
 * default (the logged-in user) back to a name would mean paging through the directory to
 * find them.
 *
 * The logged-in user is always present even before they have generated anything, so the
 * default selection can never render as a blank field.
 */
export const ReportCreatorSelect: React.FC<ReportCreatorSelectProps> = ({ value, onChange, disabled = false }) => {
  const { currentUser } = useContext(AuthContext)

  const { data } = useFetchReportCreatorsQuery({ ...GRAPHQL_QUERY_POLICY, variables: undefined })

  const options = useMemo(() => {
    const creators = (data?.fetchReportCreators || []).filter(Boolean)
    const hasCurrentUser = creators.some(creator => creator?.id === currentUser?.id)

    const withCurrentUser =
      currentUser?.id && !hasCurrentUser ? [currentUser as User, ...creators] : [...creators]

    return withCurrentUser
      .map(creator => ({ id: creator?.id as string, label: fullName(creator) }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [data, currentUser])

  const handleChange = (event: SelectChangeEvent) => onChange(event.target.value)

  // Until the options land, an id that is not yet among them would make MUI treat the Select
  // as uncontrolled and warn; fall back to rendering nothing selected for that one frame.
  const isKnown = value === QUERY_PARAMS.ALL || options.some(option => option.id === value)

  return (
    <FormControl fullWidth>
      <InputLabel>Generated By</InputLabel>
      <Select
        value={isKnown ? value : ''}
        label="Generated By"
        onChange={handleChange}
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E0E0E0'
          }
        }}
      >
        <MenuItem value={QUERY_PARAMS.ALL}>All</MenuItem>
        {options.map(option => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
